import type Point from "@arcgis/core/geometry/Point";
import Graphic from "@arcgis/core/Graphic";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import * as symbolUtils from "@arcgis/core/symbols/support/symbolUtils.js";
import { useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { CreateObjectDefaultValuesConfig } from "../../../AppConfig";
import LightIcon from "../../../assets/light.svg?react";
import LighthouseIcon from "../../../assets/lighthouse.svg?react";
import { RightPanelTabs } from "../../../constants/Panels";
import { AppContext, LighthouseContext, OverettContext, UserContext } from "../../../Context";
import {
    setCreateObjectOpen,
    setIsOpeningTool,
    setLightCalculatorOpen,
    setLoading,
    setRightPanelActiveTab,
    setRightPanelOpen,
} from "../../../store/appSlice";
import { setCurrentLighthouseTrueNorth, setIsSavingLighthouse } from "../../../store/lighthouseSlice";
import { setIsCreatingOverett, setIsEditingOverett, setOverettCreationStep } from "../../../store/overettSlice";
import { StoreState } from "../../../store/rootReducer";
import { getMapView, Tool } from "../../../utils/arcgisUtils";
import { adoptSectorsAsNew } from "../../../utils/lighthouseCreation";
import { createLighthouseAtPoint } from "../../../utils/lighthouseUtils";
import { readSectorsForFeature } from "../../../utils/sectorRead";
import NfsButton from "../../genericComponents/NfsButton/NfsButton";
import { OverettCreationStep } from "../Overett/OverettEditorContent";
import "./ObjectTypeButton.css";
import { NauticalClasses } from "../../../constants/NauticalObjects";

/** Extra context the replace flow needs when the new feature was created before being handed over. */
export interface ReplaceOptions {
    /** The feature already exists in the database, so it must not be added a second time. */
    isAlreadyCreated?: boolean;
    /** The object being replaced, for when the selected feature is no longer it. */
    replacedFeature?: Graphic | null;
}

interface ObjectTypeButtonProps {
    title: string;
    layerName: string;
    presetAttributes: CreateObjectDefaultValuesConfig[];
    setActiveLayerId?: (layerId: string) => void;
    setEditMode?: (editMode: boolean) => void;
    setNewFeature?: (feature: Graphic, options?: ReplaceOptions) => void | Promise<void>;
    setPendingLighthouseFeature?: (feature: Graphic | null) => void;
    isReplacing?: boolean;
}

const ObjectTypeButton = (props: ObjectTypeButtonProps) => {
    const {
        title,
        layerName,
        presetAttributes,
        setActiveLayerId,
        setEditMode,
        setNewFeature,
        setPendingLighthouseFeature,
        isReplacing,
    } = props;
    const dispatch = useDispatch();

    // The Fyrlykt entry is configured under its own group name, but its features live on the Lys layer
    const resolvedLayerId = layerName === "Fyrlykt" ? "Lys" : layerName;

    const [symbolHtmlElement, setSymbolHtmlElement] = useState<HTMLElement | null | undefined>(null);
    const dummyFeature = useRef<Graphic | null>(null);

    const appContext = useContext(AppContext);
    const lighthouseContext = useContext(LighthouseContext);
    const userContext = useContext(UserContext);

    const userName = userContext?.user.value?.fullName;

    const nonSourceIcon = title === "Fyrlykt" || title === "Fyrlykt med IB" ? <LighthouseIcon /> : <LightIcon />;

    const overettContext = useContext(OverettContext);
    const isCreatingOverett = useSelector((state: StoreState) => state.overett.isCreatingOverett);

    const rightPanelActiveTab = useSelector((state: StoreState) => state.app.rightPanelActiveTab);
    const hasUnsavedChanges = useSelector((state: StoreState) => state.app.hasUnsavedChanges);

    const openOverettEditor = () => {
        dispatch(setIsCreatingOverett(!isCreatingOverett));
        dispatch(setIsEditingOverett(false));
        overettContext!.overettLinjeRef.value.current = null;
        overettContext?.overettObjects.set({ upper: null, lower: null });
        overettContext?.overettHighlightsRef.clear();
        dispatch(setOverettCreationStep(OverettCreationStep.SelectObjects));

        if (isCreatingOverett) {
            dispatch(setRightPanelOpen(false));
        } else {
            dispatch(setLightCalculatorOpen({ open: false, loadFeatureValues: false }));
            dispatch(setRightPanelActiveTab(RightPanelTabs.OVERETT));

            const mapView = getMapView();
            if (!mapView) return;

            const lightLayer = mapView.map?.findLayerById("Lys");
            const merkeLayer = mapView.map?.findLayerById("Fast sjømerke");
            const overettLinjeLightLayer = mapView.map?.findLayerById("Overettlinje lys");
            const overettLinjeMerkeLayer = mapView.map?.findLayerById("Overettlinje merke");

            if (lightLayer) lightLayer.visible = true;
            if (merkeLayer) merkeLayer.visible = true;
            if (overettLinjeLightLayer) overettLinjeLightLayer.visible = true;
            if (overettLinjeMerkeLayer) overettLinjeMerkeLayer.visible = true;
        }
        dispatch(setCreateObjectOpen(false));
    };

    const openSeaCableEditor = () => {
        if (rightPanelActiveTab !== RightPanelTabs.SEA_CABLE) {
            dispatch(setIsCreatingOverett(false));
            dispatch(setLightCalculatorOpen({ open: false, loadFeatureValues: false }));
            dispatch(setRightPanelActiveTab(RightPanelTabs.SEA_CABLE));
        } else {
            dispatch(setRightPanelActiveTab(RightPanelTabs.UNDEFINED));
        }
        dispatch(setCreateObjectOpen(false));
    };

    useEffect(() => {
        const mapview = getMapView();
        if (!mapview?.map) return;

        const layer = mapview.map.findLayerById(resolvedLayerId) as FeatureLayer | GraphicsLayer;
        // Adding extra security in case of db changes or other errors
        if (!layer) return;

        dummyFeature.current = new Graphic({
            attributes:
                layer.type === "feature"
                    ? layer?.fields?.map((field) => field.name).reduce((acc, field) => ({ ...acc, [field]: null }), {})
                    : {},
            layer: layer,
        });

        presetAttributes?.forEach((presetAttribute) => {
            dummyFeature.current!.attributes[presetAttribute.FieldName] = presetAttribute.Value;
        });

        if (title !== "Fyrlykt" && title !== "Fyrlykt med IB") {
            symbolUtils.getDisplayedSymbol(dummyFeature.current).then((symbol) => {
                if (symbol) {
                    symbolUtils.renderPreviewHTML(symbol, { size: 12 }).then((symbolElement) => {
                        setSymbolHtmlElement(symbolElement);
                    });
                }
            });
        }
    }, []);

    const onClick = async () => {
        const mapview = getMapView();
        if (!mapview?.map) return;

        if (hasUnsavedChanges) {
            if (
                !globalThis.confirm(
                    "Du har lagrede endringer. Er du sikker på at du vil bytte verktøy og miste disse endringene?"
                )
            ) {
                return;
            }
        }

        setActiveLayerId?.(resolvedLayerId);
        setEditMode?.(true);
        appContext!.createObjectCurrentEntryRef.value.current = title;

        dispatch(setIsOpeningTool(Tool.CREATE_OBJECT));

        if (layerName === "Sjøkabel") {
            openSeaCableEditor();
            return;
        }

        if (layerName === "Overettlinje lys") {
            openOverettEditor();
            return;
        }

        const isFyrstasjon = dummyFeature.current?.attributes.klasse === NauticalClasses.FS_Fyrstasjon;
        const isLighthouse = layerName === "Fyrlykt" || isFyrstasjon;

        // A lighthouse is not created here anymore - the user places it on the map first, and
        // CreateObjectControl creates it at the point where the sketch completed.
        setPendingLighthouseFeature?.(isLighthouse ? dummyFeature.current : null);

        if (isLighthouse && isReplacing && dummyFeature.current) {
            // Replacing runs from the feature editor, where there is no create-object sketch
            // to place with, so the lighthouse inherits the location of the object it replaces.
            // A replacement reproduces the sectors of the object it replaces. Anything that
            // has none - a plain light, a sjømerke - falls back to the configured defaults.
            try {
                const replacedFeature = appContext?.selectedFeature.value;
                const existingSectors =
                    replacedFeature?.layer instanceof FeatureLayer
                        ? await readSectorsForFeature(replacedFeature.attributes.objectid, replacedFeature.layer)
                        : null;

                const copiedSectors =
                    existingSectors?.kind === "sectors" && existingSectors.sectors.length > 0
                        ? adoptSectorsAsNew(existingSectors.sectors)
                        : undefined;

                const created = await createLighthouseAtPoint({
                    baseFeature: dummyFeature.current,
                    center: appContext?.selectedFeature.value?.geometry as Point,
                    isFyrstasjon,
                    isReplacing: true,
                    sectors: copiedSectors,
                    userName,
                    entryTitle: appContext?.createObjectCurrentEntryRef.value.current,
                    setSelectedFeature: appContext?.selectedFeature.set,
                    setLighthouseData: (lighthouseData) => lighthouseContext?.lighthouseData.set(lighthouseData),
                    setTrueNorth: (trueNorth) => dispatch(setCurrentLighthouseTrueNorth(trueNorth)),
                    setIsSavingLighthouse: (isSaving) => dispatch(setIsSavingLighthouse(isSaving)),
                    setLoading: (loadingStatus) => dispatch(setLoading(loadingStatus)),
                });

                if (created) {
                    dummyFeature.current = created.newFeature;

                    // Hand over to the replace flow, which marks the old object as projected
                    // removed, opens it in the viewer and raises the job toast.
                    await setNewFeature?.(created.newFeature, { isAlreadyCreated: true, replacedFeature });
                }
            } catch (error) {
                console.error("Kunne ikke erstatte objektet", error);
                toast.error("Kunne ikke erstatte objektet. Prøv igjen.");
                // createLighthouseAtPoint turns the loader on and only turns it off when it
                // runs to the end, so a failure has to clear it or the overlay stays up
                dispatch(setLoading(false));
                dispatch(setIsSavingLighthouse(false));
            }
            return;
        }

        if (!dummyFeature.current) return;

        setNewFeature?.(dummyFeature.current);

        let latestObjectTypes = JSON.parse(localStorage.getItem("latestObjectTypes") ?? "[]") as string[];
        if (!latestObjectTypes) return;

        if (latestObjectTypes.includes(title)) {
            latestObjectTypes.splice(latestObjectTypes.indexOf(title), 1);
        }

        latestObjectTypes = [title, ...latestObjectTypes];
        localStorage.setItem("latestObjectTypes", JSON.stringify(latestObjectTypes));
    };

    return (
        <NfsButton className="create-object-type-button empty outline" key={title} onClick={onClick}>
            {symbolHtmlElement ? (
                <div dangerouslySetInnerHTML={{ __html: symbolHtmlElement?.outerHTML ?? "" }}></div>
            ) : (
                nonSourceIcon
            )}
            {title}
        </NfsButton>
    );
};

export default ObjectTypeButton;
